import { useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { MenuFavoriteStar } from '@/components/MenuFavoriteStar';
import { MenuIcon } from '@/components/MenuIcon';
import { useSession } from '@/lib/auth/session';
import { BRAND_COLOR } from '@/lib/brand';
import { mineFirst, resolveMySiteIds } from '@/lib/menu/siteFirst';
import { useSites } from '@/lib/queries';
import { useStoredState } from '@/lib/utils/useStoredState';
import {
  compareSitesBy,
  SITE_LIST_SORT_OPTIONS,
  SITE_SECTIONS,
  SITE_SORT_DIRECTION_LABELS,
  type SiteSortDirection,
} from '@/pages/sites/status/siteStatusData';
import { useSiteFavorites } from '@/pages/sites/status/useSiteFavorites';

/**
 * 현장 목록 — 홈 오른쪽 기둥(사용자 지시 2026-10-15 · registry 의 `placement: 'rail'`).
 *
 * 현장 현황을 거치지 않고 바로 현장 조회로 들어가는 입구다. 묶음은 국내 진행 · 해외 진행 · 미진행 ·
 * 평가 · 준공(사용자 지시 2026-10-15)이고, 묶음 머리를 누르면 접힌다 — 준공은 접힌 채 시작한다. 목록이 길면
 * 스크롤에 더해 마우스로 잡아 끌어 올리고 내린다(`useDragScroll`). 묶음 안의 순서는 현장 현황과
 * 같은 규칙이다 — 별표 먼저, 그다음 내 현장, 나머지는 현장 관리 순서(`mineFirst` 두 번).
 *
 * 넓은 화면에서는 <b>왼쪽 사이드바와 같은 몸</b>이다(「좌측 사이드바마냥 전체를 먹게」) — 흰 바탕에
 * 경계선 한 줄, 머리줄 아래부터 바닥까지. 그래서 다른 위젯의 `WidgetFrame` 카드를 쓰지 않는다.
 * 좁은 화면에서는 같은 몸이 오른쪽 서랍으로 숨는다(사용자 지시 2026-10-19 · HomePage 의 `railOpen`).
 */
export function SiteListWidget() {
  const { data: session } = useSession();
  const sites = useSites();
  const { favoriteIds, setFavorite } = useSiteFavorites();
  const mySiteIds = useMemo(() => new Set(resolveMySiteIds(session)), [session]);

  // 준공은 접힌 채 시작한다(사용자 지시 2026-10-15) — 끝난 현장이 목록 대부분을 먹는다.
  const [folded, setFolded] = useState<Set<string>>(() => new Set(['completed']));
  const drag = useDragScroll();

  // 정렬은 현장 현황과 같은 규칙(compareSitesBy)이되 저장 칸은 따로 둔다 — 홈 목록과 카드 화면을 다르게 보는 사람이 있다.
  const [storedSortKey, setStoredSortKey] = useStoredState('homeSiteList.sortKey');
  const [storedSortDirection, setStoredSortDirection] = useStoredState('homeSiteList.sortDirection');
  const sortOption = SITE_LIST_SORT_OPTIONS.find((option) => option.key === storedSortKey) ?? SITE_LIST_SORT_OPTIONS[0];
  const sortKey = sortOption.key;
  const sortDirection: SiteSortDirection =
    storedSortDirection === 'asc' || storedSortDirection === 'desc' ? storedSortDirection : sortOption.defaultDirection;

  const sections = useMemo(() => {
    const source = sites.data ?? [];
    const sorted = sortKey === 'none' ? source : [...source].sort(compareSitesBy(sortKey, sortDirection, new Map()));
    const ordered = mineFirst(mineFirst(sorted, (site) => site.id, mySiteIds), (site) => site.id, favoriteIds);
    return SITE_SECTIONS.map((section) => ({ ...section, sites: ordered.filter(section.match) })).filter(
      (section) => section.sites.length > 0,
    );
  }, [favoriteIds, mySiteIds, sites.data, sortDirection, sortKey]);
  const total = sections.reduce((sum, section) => sum + section.sites.length, 0);

  const toggle = (key: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section
      className="h-full flex flex-col bg-white border-l"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
        <MenuIcon name="building" size={14} />
        <h2 className="text-ui-md font-semibold" style={{ color: 'var(--text-primary)' }}>
          현장 리스트
        </h2>
        {total > 0 && (
          <span className="text-ui-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{total}</span>
        )}
        <Link to="/sites/status" className="ml-auto text-ui-xs" style={{ color: 'var(--text-muted)' }}>
          전체보기 ›
        </Link>
      </div>

      {/* 기준 버튼 둘. 처음 누르면 그 기준, 다시 누르면 방향만 뒤집는다 — 방향은 화살표 하나로만 보인다. */}
      <div className="flex items-center gap-1 px-3 py-1.5 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
        {SITE_LIST_SORT_OPTIONS.filter((option) => option.key !== 'none').map((option) => {
          const active = sortKey === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                if (active) {
                  setStoredSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  setStoredSortKey(option.key);
                  setStoredSortDirection(null);
                }
              }}
              aria-pressed={active}
              aria-label={active ? `${option.label} · ${SITE_SORT_DIRECTION_LABELS[sortDirection]}` : option.label}
              className="flex items-center gap-0.5 px-2 py-0.5 rounded text-ui-xs"
              style={{
                fontWeight: active ? 700 : 500,
                color: active ? 'var(--accent)' : 'var(--text-muted)',
                background: active ? 'var(--bg-hover)' : 'transparent',
              }}
            >
              {option.label}
              {active && <span style={{ fontSize: '0.8em' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
            </button>
          );
        })}
      </div>

      {sites.isPending ? null : total === 0 ? (
        <p className="px-3 py-4 text-ui-sm" style={{ color: 'var(--text-muted)' }}>등록된 현장이 없습니다.</p>
      ) : (
        <div
          ref={drag.ref}
          className="home-site-rail-list py-1"
          onMouseDown={drag.onMouseDown}
          onClickCapture={drag.onClickCapture}
          style={{ cursor: drag.dragging ? 'grabbing' : undefined, userSelect: drag.dragging ? 'none' : undefined }}
        >
          {sections.map((section) => {
            const open = !folded.has(section.key);
            return (
              <div key={section.key}>
                <button
                  type="button"
                  onClick={() => toggle(section.key)}
                  className="w-full flex items-center gap-1.5 px-3 pt-2 pb-1 text-ui-md font-bold text-left"
                  style={{ color: BRAND_COLOR.red }}
                  aria-expanded={open}
                >
                  <span style={{ display: 'inline-flex', transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.15s' }}>
                    <MenuIcon name="chevron-right" size={11} />
                  </span>
                  {section.label}
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{section.sites.length}</span>
                </button>
                {open && (
                  <ul>
                    {section.sites.map((site) => {
                      const favorite = favoriteIds.has(site.id);
                      return (
                      <li key={site.id} className="group flex items-center pl-1.5 hover:bg-[var(--bg-hover)]">
                        {/* 별은 링크 밖 형제 — 사이드바 메뉴와 같은 손잡이. 안 담긴 줄은 손을 얹을 때만 선다. */}
                        <MenuFavoriteStar
                          on={favorite}
                          label={site.name}
                          size={12}
                          style={{ padding: '4px' }}
                          className={favorite ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
                          onToggle={() => setFavorite(site.id, !favorite)}
                        />
                        <Link
                          to={`/sites/pmis?site=${site.id}`}
                          className="flex-1 min-w-0 flex items-center gap-1.5 pr-3 py-1.5 text-ui-sm"
                          style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                          title={site.name}
                        >
                          <span className="truncate" style={{ fontWeight: mySiteIds.has(site.id) ? 700 : 500 }}>{site.name}</span>
                          <span className="ml-auto flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                            <MenuIcon name="chevron-right" size={12} />
                          </span>
                        </Link>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * 마우스로 잡아 끌어 스크롤한다. 터치는 브라우저가 이미 끌어 올리므로 마우스만 받는다.
 *
 * ★ 몇 픽셀 이상 끌었으면 손을 뗄 때의 클릭을 삼킨다 — 안 그러면 끌기를 끝낸 자리의 현장으로 들어가 버린다.
 */
function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const moved = useRef(false);
  const [dragging, setDragging] = useState(false);

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const box = ref.current;
    if (event.button !== 0 || !box || box.scrollHeight <= box.clientHeight) return;
    const startY = event.clientY;
    const startTop = box.scrollTop;
    moved.current = false;

    const onMove = (move: globalThis.MouseEvent) => {
      const delta = move.clientY - startY;
      if (!moved.current && Math.abs(delta) < 4) return;
      if (!moved.current) {
        moved.current = true;
        setDragging(true);
      }
      move.preventDefault();
      box.scrollTop = startTop - delta;
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!moved.current) return;
    moved.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return { ref, dragging, onMouseDown, onClickCapture };
}
